'use client';
import ProductActions from '@components/ProductComparison/_ProductActions';
import CompareSection, { Props as SectionProps } from '@components/ProductComparison/_Section';
import Section from '@repo/ui/src/components/Section';
import React, { useState, useEffect } from 'react';
import { Props as SeoProps } from '@repo/ui/src/components/Seo';
import { ImageProps } from '@repo/ui/src/types';
import Product from '@models/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import { ProductModelProps } from '@typings/product';
import useProductList from '@hooks/product/useProductList';

export interface ProductComparisonProps {
  sections?: Array<SectionProps>;
  skus: Array<string>;
  seo?: SeoProps;
  pageTitle: string;
  _testID: string;
  noSelectionImage: ImageProps;
  asContentRow?: boolean;
  _data?: Array<ProductModelProps>;
}

const ProductComparison = ({
  pageTitle,
  sections,
  skus,
  _testID,
  noSelectionImage,
  _data,
  asContentRow,
}: ProductComparisonProps) => {
  const mdAndUp = useIsMD();

  const { data } = useProductList({ skus });
  const products = (data ?? _data)?.map((product: ProductModelProps) => new Product(product)) ?? [];
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);

  useEffect(() => {
    let selectedSkus = !mdAndUp ? skus.slice(0, 2) : [...skus.slice(0, 2), ''];
    if (asContentRow) {
      selectedSkus = !mdAndUp ? skus.slice(0, 2) : skus.slice(0, 4);
    }
    setSelectedSkus(selectedSkus);
  }, [mdAndUp]);

  const HeadingTag = asContentRow ? 'h2' : 'h1';

  return (
    <Section texture>
      <div data-test-id={`hero-wrapper_${_testID}`} className="tw-py-10 md:tw-py-20 tw-text-center">
        <HeadingTag
          className={classNames(
            'tw-text-neutral-500',
            asContentRow ? 'tra-text-web-heading-2' : 'tra-text-web-heading-1',
          )}
        >
          {pageTitle}
        </HeadingTag>
      </div>

      <div className="tw-bg-white md:tw-mx-10 md:tw-py-20 md:tw-px-10">
        <div className="tw-w-full">
          {!!products?.length && (
            <div>
              <ProductActions
                selectedSkus={selectedSkus}
                selectableSkus={skus}
                products={products}
                noSelectionImage={noSelectionImage}
                updateSelectedProducts={(sku, index) => {
                  setSelectedSkus(selectedSkus.map((oldSku, i) => (index === i ? sku : oldSku)));
                }}
                _testID={_testID}
                asContentRow={asContentRow}
              />

              {sections?.map((section, idx) => (
                <CompareSection
                  {...section}
                  key={idx}
                  selectedSkus={selectedSkus}
                  products={products}
                  asContentRow={asContentRow}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default ProductComparison;
